export const YAO_COUNT = 6;
export const YAO_NAMES = ['初爻', '二爻', '三爻', '四爻', '五爻', '上爻'];

/**
 * 八卦（三爻卦）名称映射表
 * 1 = 阳, 0 = 阴 (键: 爻序：从下到上 [下爻][中爻][上爻]) 🌟 修正注释
 */
export const TRIGRAM_MAP = {
    // 乾三连，坤六断，震仰盂，艮覆碗，离中虚，坎中满，兑上缺，巽下断
    '111': '乾', // 乾为天
    '110': '兑', // 泽 — 兑上缺: 初阳二阳三阴 → [初][二][三]=110
    '101': '离', // 火 — 离中虚: 初阳二阴三阳 → 101
    '100': '震', // 雷 — 震仰盂: 初阳二阴三阴 → 100

    '011': '巽', // 风 — 巽下断: 初阴二阳三阳 → 011
    '010': '坎', // 水 — 坎中满: 初阴二阳三阴 → 010
    '001': '艮', // 山 — 艮覆碗: 初阴二阴三阳 → 001
    '000': '坤'  // 地
};

/**
 * 八卦名称到其三爻二进制码的反向映射表
 * 码序: [下爻][中爻][上爻] 🌟 修正注释
 */
export const TRIGRAM_REVERSE_MAP = {
    '乾': '111', '兑': '110', '离': '101', '震': '100',
    '巽': '011', '坎': '010', '艮': '001', '坤': '000'
};

/**
 * 完整的六十四卦名映射表 (键: 外卦+内卦)
 */
export const HEXAGRAM_MAP = {
    // 外卦：乾 (Qian) - 天
    '乾乾': '乾为天', '乾兑': '天泽履', '乾离': '天火同人', '乾震': '天雷无妄', '乾巽': '天风姤', '乾坎': '天水讼', '乾艮': '天山遁', '乾坤': '天地否',
    // 外卦：兑 (Dui) - 泽
    '兑乾': '泽天夬', '兑兑': '兑为泽', '兑离': '泽火革', '兑震': '泽雷随', '兑巽': '泽风大过', '兑坎': '泽水困', '兑艮': '泽山咸', '兑坤': '泽地萃',
    // 外卦：离 (Li) - 火
    '离乾': '火天大有', '离兑': '火泽睽', '离离': '离为火', '离震': '火雷丰', '离巽': '火风鼎', '离坎': '火水既济', '离艮': '火山旅', '离坤': '火地晋',
    // 外卦：震 (Zhen) - 雷
    '震乾': '雷天大壮', '震兑': '雷泽归妹', '震离': '雷火丰', '震震': '震为雷', '震巽': '雷风恒', '震坎': '雷水解', '震艮': '雷山小过', '震坤': '雷地豫',
    // 外卦：巽 (Xun) - 风
    '巽乾': '风天小畜', '巽兑': '风泽中孚', '巽离': '风火家人', '巽震': '风雷益', '巽巽': '巽为风', '巽坎': '风水涣', '巽艮': '风山渐', '巽坤': '风地观',
    // 外卦：坎 (Kan) - 水
    '坎乾': '水天需', '坎兑': '水泽节', '坎离': '水火既济', '坎震': '水雷屯', '坎巽': '水风井', '坎坎': '坎为水', '坎艮': '水山蹇', '坎坤': '水地比',
    // 外卦：艮 (Gen) - 山
    '艮乾': '山天大畜', '艮兑': '山泽损', '艮离': '山火贲', '艮震': '山雷颐', '艮巽': '山风蛊', '艮坎': '山水蒙', '艮艮': '艮为山', '艮坤': '山地剥',
    // 外卦：坤 (Kun) - 地
    '坤乾': '地天泰', '坤兑': '地泽临', '坤离': '地火明夷', '坤震': '地雷复', '坤巽': '地风升', '坤坎': '地水师', '坤艮': '地山谦', '坤坤': '坤为地'
};

/**
 * 辅助函数：根据硬币数量（6, 7, 8, 9）返回爻的符号和状态
 * 此函数用于详细展示爻的属性。
 * @param {number} yao - 投掷结果 (6, 7, 8, 9)
 * @returns {{coin: string, line: string, name: string, isMoving: boolean, isYang: boolean}}
 */
export const getSymbolData = (yao) => {
    // 🌟 修正 1: 将 coins 初始值改为数组
    let coins = []; 
    let line = '';
    let name = '';
    let isMoving = false; // 🌟 新增：判断是否为动爻
    let isYang = false;   // 🌟 新增：判断本爻是否为阳爻
    
    // 假设：🌕 = 正面 (阳)；🌑 = 反面 (阴)
    switch (yao) {
        case 6: // 老阴 (三反，变阳) - 本卦显示阴爻
            // 🌟 修正 2: coins 从字符串改为数组
            coins = ['🌑', '🌑', '🌑']; 
            line = '— —';  
            name = '老阴';
            isMoving = true; // 动爻
            isYang = false;  // 本爻是阴爻
            break;
            
        case 7: // 少阳 (两阳一阴，不变) - 本卦显示阳爻
            // 🌟 修正 2: coins 从字符串改为数组
            coins = ['🌕', '🌕', '🌑']; 
            line = '———';  
            name = '少阳';
            isMoving = false; // 静爻
            isYang = true;   // 本爻是阳爻
            break;
            
        case 8: // 少阴 (两阴一阳，不变) - 本卦显示阴爻
            // 🌟 修正 2: coins 从字符串改为数组
            coins = ['🌑', '🌑', '🌕']; 
            line = '— —';  
            name = '少阴';
            isMoving = false; // 静爻
            isYang = false;  // 本爻是阴爻
            break;
            
        case 9: // 老阳 (三阳，变阴) - 本卦显示阳爻
            // 🌟 修正 2: coins 从字符串改为数组
            coins = ['🌕', '🌕', '🌕']; 
            line = '———';  
            name = '老阳';
            isMoving = true; // 动爻
            isYang = true;   // 本爻是阳爻
            break;
            
        default:  
            coins = [];
            line = '';
            name = '';
            isMoving = false;
            isYang = false;
    }
    
    return { coins, line, name, isMoving, isYang };
};

/**
 * 获取变卦后的爻值 (1=阳, 0=阴)
 * @param {number} yao - 投掷结果 (6, 7, 8, 9)
 * @returns {number} 1 (阳) 或 0 (阴)
 */
export function getBinaryYao(yao) {
    // 6 (老阴) 变阳 (1), 7 (少阳) 不变 (1)
    if (yao === 6 || yao === 7) return 1;  
    // 8 (少阴) 不变 (0), 9 (老阳) 变阴 (0)
    if (yao === 8 || yao === 9) return 0;
    
    return 1;
}

/**
 * 根据卦键（外卦+内卦名称）获取其 6 位二进制码数组
 * @param {string} key - e.g., '乾兑'
 * @returns {number[]} 6位二进制码数组 (Y0, Y1, Y2, Y3, Y4, Y5) 从初爻(0)到上爻(5)
 */
function getHexagramBinaryCode(key) {
    const outerName = key.substring(0, key.length - 1);
    const innerName = key.substring(key.length - 1);
    
    // 获取三爻卦的二进制码 (码序: [下爻][中爻][上爻])
    const outerCode = TRIGRAM_REVERSE_MAP[outerName]; // e.g., 艮: '001'
    const innerCode = TRIGRAM_REVERSE_MAP[innerName]; // e.g., 乾: '111'

    // 🌟 修正：TRIGRAM_REVERSE_MAP 现在是 [下][中][上]，无需 reverse
    // 故：[下爻]Y0, [中爻]Y1, [上爻]Y2 (Inner)
    const innerYaoOrder = innerCode.split(''); // ['1', '1', '1']
    // 故：[下爻]Y3, [中爻]Y4, [上爻]Y5 (Outer)
    const outerYaoOrder = outerCode.split(''); // ['0', '0', '1']

    // 组合并转换为数字数组 (Y0, Y1, Y2, Y3, Y4, Y5)
    return [...innerYaoOrder, ...outerYaoOrder].map(Number);
}

/**
 * 核心反推逻辑：根据本卦和变卦的爻线状态，反推出投掷结果 (6, 7, 8, 9)
 * @param {number[]} benCodeArray - 本卦的 6 位二进制爻线 (0/1)
 * @param {number[]} bianCodeArray - 变卦的 6 位二进制爻线 (0/1)
 * @returns {number[]} 6个硬币投掷结果数组 (6, 7, 8, 9)
 */
function deriveYaoValues(benCodeArray, bianCodeArray) {
    const yaoValues = [];
    for (let i = 0; i < YAO_COUNT; i++) {
        const ben = benCodeArray[i]; // 本爻阴阳 (0/1)
        const bian = bianCodeArray[i]; // 变爻阴阳 (0/1)
        
        if (ben === bian) {
            // 状态不变：少阳(7) 或 少阴(8)
            // 少阳: 本卦为阳(1) 且 变卦为阳(1) -> 7
            // 少阴: 本卦为阴(0) 且 变卦为阴(0) -> 8
            yaoValues.push(ben === 1 ? 7 : 8);
        } else {
            // 状态改变：老阳(9) 或 老阴(6)
            // 老阳: 本卦为阳(1) 变阴(0) -> 9
            // 老阴: 本卦为阴(0) 变阳(1) -> 6
            yaoValues.push(ben === 1 ? 9 : 6);
        }
    }
    return yaoValues;
}

/**
 * 核心功能：模拟六爻投掷过程，但使用“卦 -> 爻”的反推逻辑。
 * 引入权重和最大重试次数，控制多变爻（>=4）的出现概率，使其极小化。
 * @returns {{yaoValues: Array<number>, mainHexagram: string, changeHexagram: string}}
 */
export function generateHexagramsAndDeriveYao() {
    // 定义不同变爻数量的接受权重（百分比）
    // 🌟 目标：提高静卦比例，降低少动爻的接受率
    const MOVING_YAO_WEIGHTS = {
        0: 100, // 零动爻（静卦）必须 100% 接受
        
        // 降低接受率，以减少变爻的整体出现频率
        1: 50,  // 50% 几率接受一个动爻
        2: 30,  // 30% 几率接受两个动爻
        3: 20,  // 20% 几率接受三个动爻
        
        // 极低几率接受多动爻
        4: 10,  // 10% 几率接受
        5: 3,   // 3% 几率接受
        6: 1    // 1% 几率接受
    };

    const MAX_RETRIES = 1000; // 🌟 新增：最大重试次数限制

    const allHexagramKeys = Object.keys(HEXAGRAM_MAP);
    
    let benKey, bianKey, benGuaName, bianGuaName, benCodeArray, bianCodeArray, yaoValues, movingYaoCount;

    // 🌟 初始化重试计数器
    let accepted = false;
    let retryCount = 0;
    
    while (!accepted) {
        // 1. 纯随机选择本卦和变卦
        benKey = allHexagramKeys[Math.floor(Math.random() * allHexagramKeys.length)];
        bianKey = allHexagramKeys[Math.floor(Math.random() * allHexagramKeys.length)];

        benGuaName = HEXAGRAM_MAP[benKey];
        bianGuaName = HEXAGRAM_MAP[bianKey];
        benCodeArray = getHexagramBinaryCode(benKey);
        bianCodeArray = getHexagramBinaryCode(bianKey);

        // 2. 反推出爻值并计算动爻数量
        yaoValues = deriveYaoValues(benCodeArray, bianCodeArray);
        movingYaoCount = yaoValues.filter(yao => yao === 6 || yao === 9).length;

        // 3. 检查并应用权重
        const weight = MOVING_YAO_WEIGHTS[movingYaoCount] || 0; 
        const randomRoll = Math.random() * 100;

        if (randomRoll < weight) {
            // 满足权重条件，接受结果
            accepted = true;
        } 
        
        // 🌟 检查重试次数
        retryCount++;
        if (retryCount >= MAX_RETRIES) {
            // 达到最大重试次数，强制接受当前结果并跳出循环
            accepted = true; 
            // 可选：在此处添加日志警告
            // console.warn(`Hexagram generation hit max retries (${MAX_RETRIES}). Accepting current result with ${movingYaoCount} moving yao.`);
        }
    }

    // 4. 确定最终结果
    return {
        yaoValues: yaoValues,
        mainHexagram: benGuaName,
        changeHexagram: bianGuaName
    };
}

/**
 * 【保留】根据六个爻的结果确定卦名
 * 此函数现在主要用于辅助验证和详细结果处理。
 * @param {Array<number>} yaoValues - 六个爻的硬币结果数组 (6, 7, 8, 9)
 * @param {boolean} isBianGua - 是否计算变卦的卦名
 * @returns {string} 卦名
 */
export function getHexagramName(yaoValues, isBianGua = false) {
    if (yaoValues.length !== YAO_COUNT) return '爻数不足';

    // 确定用于查卦的爻线值 (0或1)
    let binaryYao = yaoValues.map(yao => {
        if (!isBianGua) {
            // 本卦：只看阴阳属性 (7, 9 -> 阳爻 1； 6, 8 -> 阴爻 0)
            return getSymbolData(yao).isYang ? 1 : 0;
        } else {
            // 变卦：使用变爻后的阴阳 (getBinaryYao)
            return getBinaryYao(yao);
        }
    });
    
    const innerCode = binaryYao.slice(0, 3).join('');  // [Y0, Y1, Y2] 
    const outerCode = binaryYao.slice(3, 6).join('');  // [Y3, Y4, Y5]
    
    const innerTrigram = TRIGRAM_MAP[innerCode] || '—';
    const outerTrigram = TRIGRAM_MAP[outerCode] || '—';

    const hexagramKey = outerTrigram + innerTrigram;
    return HEXAGRAM_MAP[hexagramKey] || `（${outerTrigram}上${innerTrigram}下）`;
}


/**
 * 【保留】根据六个爻的结果（6, 7, 8, 9）确定最终的卦象结果
 * 此函数现在主要用于生成详细的爻数据列表。
 * @param {Array<number>} yaoValues - 六个爻的硬币结果数组 (6, 7, 8, 9)
 * @returns {{benGuaName: string, bianGuaName: string, yaoDetails: Array<Object>, movingYaoCount: number}}
 */
export function determineSixYaoResults(yaoValues) {
    if (!yaoValues || yaoValues.length !== YAO_COUNT) {
        return {
            benGuaName: '爻数错误',
            bianGuaName: '爻数错误',
            yaoDetails: [],
            movingYaoCount: 0,
        };
    }

    // 1. 获取本卦（原卦）和变卦名称 (使用原有的计算逻辑，用于验证和数据展示)
    const benGuaName = getHexagramName(yaoValues, false);
    const bianGuaName = getHexagramName(yaoValues, true);

    // 2. 确定每个爻的详细信息
    let movingYaoCount = 0;
    const yaoDetails = yaoValues.map((yao, index) => {
        const symbolData = getSymbolData(yao);
        if (symbolData.isMoving) {
            movingYaoCount++;
        }
        return {
            index,
            name: YAO_NAMES[index], // '初爻', '二爻', ...
            yaoValue: yao, // 6, 7, 8, 9
            ...symbolData, // coin, line, name, isMoving, isYang
            binaryBen: symbolData.isYang ? 1 : 0, // 本卦阴阳 (1=阳, 0=阴)
            binaryBian: getBinaryYao(yao) // 变卦阴阳 (1=阳, 0=阴)
        };
    });

    return {
        benGuaName,
        bianGuaName,
        yaoDetails,
        movingYaoCount
    };
}
